/* eslint-disable @typescript-eslint/max-params */
/* eslint-disable @typescript-eslint/prefer-destructuring */
import type { Map } from "@mapka/react-maplibre-map";
import { getCurrentVersion } from "./tileset";
import type { Tileset, TilesetDataset, TilesetVersion, TilesetWithVersions } from "../types/tileset";
import type { SourceSpecification } from "@maplibre/maplibre-gl-style-spec";
import type { MapkaLayerSpecification } from "@mapka/maplibre-gl-sdk";
import { API_HOST } from "../../../../constants/url";
import type { AccountInfo } from "../../../../types/account";

const colors = {
  cyan: "#33DBFF",
  coral: "#FF6A33",
  indigo: "#5733FF",
  limeGreen: "#75FF33",
  magenta: "#FF33A8",
  yellowGreen: "#DBFF33",
  deepViolet: "#6A33FF",
  orange: "#FFBD33",
  turquoise: "#33FFD4",
  hotPink: "#FF336A",
  blue: "#3375FF",
  gold: "#FFD433",
  emerald: "#33FF6A",
  redOrange: "#FF5733",
  skyBlue: "#339AFF",
  purple: "#BD33FF",
  mint: "#33FFBD",
  pink: "#FF33DB",
  chartreuse: "#B6FF33",
  green: "#33FF57",
};

const colorsValues = Object.values(colors);

function trimBy(text: string, x: number) {
  return text
    .split("\n")
    .map(line => line.slice(x))
    .join("\n");
}

export function getSourceUrl(
  account: AccountInfo,
  tileset: Tileset,
  version: TilesetVersion,
  dataset: TilesetDataset | null,
  apiKey?: string,
) {
  const {
    name: accountName,
  } = account;
  const {
    id: versionId,
  } = version;

  const {
    key: tilesetKey,
    currentVersionId,
  } = tileset;

  const apiKeySearch = apiKey ? `?apiKey=${apiKey}` : "";

  const path = version.format === "geojson"
    ? "data.geojson"
    : "tile.json";

  const tiles = versionId == currentVersionId
    ? dataset
      ? `https://${API_HOST}/v1/${accountName}/datasets/${dataset.key}/tilesets/${tilesetKey}/${path}${apiKeySearch}`
      : `https://${API_HOST}/v1/${accountName}/tilesets/${tilesetKey}/${path}${apiKeySearch}`
    : dataset
      ? `https://${API_HOST}/v1/${accountName}/datasets/${dataset.key}/tilesets/${tilesetKey}/${path}?versionId=${versionId}${apiKeySearch}`
      : `https://${API_HOST}/v1/${accountName}/tilesets/${tilesetKey}/${path}?versionId=${versionId}${apiKeySearch}`;

  return tiles;
}

export function getExampleStyle(apiKey?: string): string {
  return apiKey
    ? `https://${API_HOST}/v1/maputnik/styles/osm-liberty.json?apiKey=${apiKey}`
    : `https://${API_HOST}/v1/maputnik/styles/osm-liberty.json`;
}

export function createTilesetExample(tilesetWithVersions?: TilesetWithVersions, apiKey?: string): undefined | { code: string } {
  if (!tilesetWithVersions || !apiKey) {
    return;
  }
  const {
    dataset,
    tileset,
    account,
  } = tilesetWithVersions;

  const version = getCurrentVersion(tilesetWithVersions);

  if (version?.status === "ready") {
    if (version.format === "mvt") {
      const {
        metadata: {
          vectorLayers,

          tilestats,
        },
      } = version;

      const tiles = getSourceUrl(account, tileset, version, dataset, apiKey);

      let code = trimBy(`
      map.addSource('${tileset.key}', {
        'type': 'vector',
        'url': '${tiles}'
      });
    `, 6);

      vectorLayers.forEach((layer, index) => {
        let geometry = "LineString";

        const layerStats = tilestats.layers.find(l => l.layer === layer.id);
        if (layerStats) {
          geometry = layerStats.geometry;
        }

        const color = colorsValues[index] ?? "#f08";

        if (geometry === "LineString") {
          code += trimBy(`
        map.addLayer(
          {
            'id': '${layer.id}',
            'type': 'line',
            'source-layer': '${layer.id}',
            'source': '${tileset.key}',
            'layout': {},
            'paint': {
              'line-color': '${color}',
              'line-opacity': 0.4,
            }
          },
        );
        `, 8);
        }
        else if (geometry === "Point") {
          code += trimBy(`
        map.addLayer(
          {
            'id': '${layer.id}',
            'type': 'circle',
            'source-layer': '${layer.id}',
            'source': '${tileset.key}',
            'layout': {},
            'paint': {
              'circle-radius': 5,
              'circle-color': '${color}'
            }
          },
        );
        `, 8);
        }
        else {
          code += trimBy(`
        map.addLayer(
          {
            'id': '${layer.id}',
            'type': 'fill',
            'source-layer': '${layer.id}',
            'source': '${tileset.key}',
            'layout': {},
            'paint': {
              'fill-color': '${color}',
              'fill-opacity': 0.4,
            }
          },
        );
        `, 8);
        }
      });
      return {
        code,
      };
    }
    else if (version.format !== "geojson") {
      const {
        metadata: {
          maxZoom,
        },
      } = version;
      const tiles = getSourceUrl(account, tileset, version, dataset, apiKey);

      let code = trimBy(`
      map.addSource('${tileset.key}', {
        'type': 'raster',
        'url': '${tiles}',
        'tileSize': 512,
        'maxzoom': ${maxZoom},
      });
    `, 6);

      code += trimBy(`
        map.addLayer(
          {
            'id': '${tileset.key}',
            'type': 'raster',
            'source': '${tileset.key}',
          },
        );
      `, 8);

      return {
        code,
      };
    }
    else {
      const geojson = getSourceUrl(account, tileset, version, dataset, apiKey);
      let code = trimBy(`
      map.addSource('${tileset.key}', {
        'type': 'geojson',
        'url': '${geojson}',
      });
    `, 6);

      code += trimBy(`
        map.addLayer(
          {
            'id': '${tileset.key}',
            'type': 'fill',
            'source': '${tileset.key}',
            'layout': {},
            'paint': {
              'fill-color': '#33DBFF',
              'fill-opacity': 0.4,
            },
          },
        );
      `, 8);

      return {
        code,
      };
    }
  }
  return;
}

export function getSourceAndLayers(
  account: AccountInfo,
  tileset: Tileset,
  version: TilesetVersion,
  dataset: TilesetDataset | null,
  apiKey?: string,
) {
  const {
    key,
  } = tileset;

  const sources: Record<string, SourceSpecification> = {};
  const layers: MapkaLayerSpecification[] = [];

  if (version.metadata && version.format === "mvt") {
    const {
      metadata: {
        vectorLayers,
        tilestats,
      },
    } = version;

    const tiles = getSourceUrl(account, tileset, version, dataset, apiKey);

    sources[key] = {
      type: "vector",
      url: tiles,
    };

    vectorLayers.forEach((layer, index) => {
      let geometry = "LineString";

      const layerStats = tilestats.layers.find(l => l.layer === layer.id);
      if (layerStats) {
        geometry = layerStats.geometry;
      }

      const color = colorsValues[index] ?? "#f08";

      if (geometry === "LineString") {
        layers.push({
          "id": layer.id,
          "type": "line",
          "source-layer": layer.id,
          "source": key,
          "layout": {},
          "paint": {
            "line-color": color,
            "line-opacity": 0.4,
          },
        });
      }
      else if (geometry === "Point") {
        layers.push({
          "id": layer.id,
          "type": "circle",
          "source-layer": layer.id,
          "source": key,
          "layout": {},
          "paint": {
            "circle-radius": 5,
            "circle-color": color,
          },
        });
      }
      else {
        layers.push({
          "id": layer.id,
          "type": "fill",
          "source-layer": layer.id,
          "source": key,
          "layout": {},
          "paint": {
            "fill-color": color,
            "fill-opacity": 0.4,
          },
        });
      }
    });
  }

  return {
    sources,
    layers,
  };
}

function getZoom(version: TilesetVersion) {
  if (version.format === "mvt" && version.status === "ready") {
    const {
      metadata: {
        minZoom,
      },
    } = version;

    return minZoom;
  }
  return 0;
}

function getCenter(version: TilesetVersion): [number, number] {
  if (version.format === "mvt" && version.status === "ready") {
    const {
      metadata: {
        center,
      },
    } = version;

    return center.slice(0, 2) as [number, number];
  }
  return [0, 0];
}

export const addTilesetToMap = (
  map: Map,
  account: AccountInfo,
  tileset: Tileset,
  dataset: TilesetDataset | null,
  version: TilesetVersion | null,
  apiKey?: string,
) => {
  if (!version) {
    return;
  }
  const { sources, layers } = getSourceAndLayers(
    account,
    tileset,
    version,
    dataset,
    apiKey,
  );
  const zoom = getZoom(version);
  map.setZoom(zoom);

  const center = getCenter(version);
  map.setCenter(center);

  Object.entries(sources).forEach(([name, source]) => {
    map.addSource(name, source);
  });

  layers.forEach((layer) => {
    map.addLayer(layer);
  });
};
